import { useState, useEffect } from 'react';

interface PWADeferredPrompt {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const usePWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<PWADeferredPrompt | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);

  useEffect(() => {
    // Detectar si la app es instalable
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as any);
      setIsInstallable(true);
    };

    // Detectar si ya está instalada
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    // Detectar cambios en la conexión
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    // Detectar actualizaciones del service worker
    const handleUpdateFound = () => {
      setIsUpdateAvailable(true);
    };

    // Event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Verificar si ya está instalada
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Verificar si hay actualizaciones del service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('updatefound', handleUpdateFound);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('updatefound', handleUpdateFound);
      }
    };
  }, []);

  // Función para instalar la PWA
  const installPWA = async (): Promise<boolean> => {
    if (!deferredPrompt) {
      console.log('No hay prompt de instalación disponible');
      return false;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('✅ Usuario aceptó instalar la PWA');
        setDeferredPrompt(null);
        setIsInstallable(false);
        return true;
      } else {
        console.log('❌ Usuario rechazó instalar la PWA');
        return false;
      }
    } catch (error) {
      console.error('Error durante la instalación:', error);
      return false;
    }
  };

  // Función para actualizar la PWA
  const updatePWA = async (): Promise<void> => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration && registration.waiting) {
        // Enviar mensaje al service worker para actualizar
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        
        // Recargar la página después de la actualización
        window.location.reload();
      }
    }
  };

  // Función para verificar el estado del service worker
  const checkServiceWorkerStatus = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        return {
          isRegistered: !!registration,
          isActive: !!registration?.active,
          isWaiting: !!registration?.waiting,
          scope: registration?.scope
        };
      } catch (error) {
        console.error('Error verificando service worker:', error);
        return null;
      }
    }
    return null;
  };

  return {
    // Estado
    isInstallable,
    isInstalled,
    isOnline,
    isUpdateAvailable,
    
    // Funciones
    installPWA,
    updatePWA,
    checkServiceWorkerStatus,
    
    // Datos
    deferredPrompt: !!deferredPrompt
  };
};
